import { useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ChevronDown,
  Search,
  Download,
  X,
  Star,
  Ban,
} from 'lucide-react'

import './CustomersPage.scss'

// ─── Mock data ────────────────────────────────────────────────────────────────

const customers = [
  {
    id: 1,
    name: 'Chen Xiao Hui',
    email: 'chenxiaohui@gmail.com',
    phone: '+6017-234 5566',
    status: 'active',
    points: 1840,
    source: 'Dine-in',
    orders: 88,
    totalSpend: 'RM4,241.00',
    avgOrder: 'RM133.00',
    memberSince: 'Oct 2025',
    signupSource: 'Dine-in',
    created: '6 May 2026 11:02 AM',
    lastLogin: '12/05/2026',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80',
  },
  {
    id: 2,
    name: 'Ali b. Ahmad',
    email: 'ali.ahmad@gmail.com',
    phone: '+60355667788',
    status: 'active',
    points: 640,
    source: 'Kiosk',
    orders: 27,
    totalSpend: 'RM1,820.00',
    avgOrder: 'RM67.40',
    memberSince: 'Jan 2025',
    signupSource: 'Kiosk',
    created: '3 Jan 2025 9:14 AM',
    lastLogin: '12/05/2026',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&q=80',
  },
  {
    id: 3,
    name: 'Ethan Goh Jee Hao',
    email: 'ethangoh@gmail.com',
    phone: '+60355667788',
    status: 'active',
    points: 210,
    source: 'Pickup',
    orders: 14,
    totalSpend: 'RM840.00',
    avgOrder: 'RM60.00',
    memberSince: 'Mar 2025',
    signupSource: 'Pickup',
    created: '15 Mar 2025 2:30 PM',
    lastLogin: '12/05/2026',
    avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=200&q=80',
  },
  {
    id: 4,
    name: 'Ravi Kumar',
    email: 'ravikumar@gmail.com',
    phone: '+60355667788',
    status: 'suspended',
    points: 20,
    source: 'Kiosk',
    orders: '-',
    totalSpend: 'RM0.00',
    avgOrder: '-',
    memberSince: 'Apr 2026',
    signupSource: 'Kiosk',
    created: '2 Apr 2026 10:00 AM',
    lastLogin: '12/05/2026',
    avatar: 'https://images.unsplash.com/photo-1511367461989-f85a21fda167?w=200&q=80',
  },
]

const CUSTOMER_ORDERS = {
  1: [
    { id: '#ORD-2025-04819', posStatus: 'open',      type: 'Pickup',  total: 'RM66.40',  placed: '15/05/2026 11:53 AM' },
    { id: '#ORD-2025-04817', posStatus: 'closed',     type: 'Dine-in', total: 'RM126.60', placed: '07/02/2026 06:08 PM' },
    { id: '#ORD-2025-04815', posStatus: 'cancelled',  type: 'Pickup',  total: 'RM148.80', placed: '07/02/2026 11:45 AM' },
    { id: '#ORD-2025-04813', posStatus: 'closed',     type: 'Kiosk',   total: 'RM120.50', placed: '17/01/2026 12:02 PM' },
  ],
}

const CUSTOMER_POINTS = {
  1: [
    { type: 'order',      label: 'Order placed',       date: '03/04/2026 11:59 AM', delta: +40, balance: 100, reason: null },
    { type: 'redeem',     label: 'Voucher redeemed',   date: '16/03/2026 12:14 PM', delta: -20, balance: 60,  reason: null },
    { type: 'adjustment', label: 'Points adjustment',  date: '11/02/2026 03:09 PM', delta: +30, balance: 80,  reason: 'Correction - duplicate points' },
    { type: 'order',      label: 'Order placed',       date: '07/02/2026 11:45 AM', delta: +50, balance: 50,  reason: null },
  ],
}

const ADJUST_REASONS = [
  'Correction - Duplicate points',
  'Goodwill gesture',
  'Promotional reward',
  'Event bonus',
  'Other',
]

// ─── Adjust Points Modal ──────────────────────────────────────────────────────

function AdjustPointsModal({ customer, onClose }) {
  const [adjType, setAdjType] = useState('add')
  const [quantity, setQuantity] = useState('')
  const [reason, setReason] = useState(ADJUST_REASONS[0])
  const [remarks, setRemarks] = useState('')

  return createPortal(
    <div className="cdp-modal-overlay" onClick={onClose}>
      <div className="cdp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cdp-modal__header">
          <h3>Adjust points</h3>
          <button className="cdp-modal__close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="cdp-modal__body">
          {/* Customer card */}
          <div className="cdp-modal__customer-card">
            <img src={customer.avatar} alt={customer.name} />
            <div>
              <p className="cdp-modal__card-name">{customer.name}</p>
              <p className="cdp-modal__card-pts"><Star size={14} className="cdp-modal__star" /> {customer.points.toLocaleString()} pts</p>
            </div>
            <span className="cdp-modal__card-status customers-status customers-status--active">• Active</span>
          </div>

          {/* Adjustment type */}
          <div className="cdp-modal__field">
            <label className="cdp-modal__label">Adjustment type</label>
            <div className="cdp-modal__radio-row">
              <label className="cdp-modal__radio">
                <input type="radio" name="adjType" value="add" checked={adjType === 'add'} onChange={() => setAdjType('add')} />
                <span className="cdp-modal__radio-circle" />
                Add points
              </label>
              <label className="cdp-modal__radio">
                <input type="radio" name="adjType" value="deduct" checked={adjType === 'deduct'} onChange={() => setAdjType('deduct')} />
                <span className="cdp-modal__radio-circle" />
                Deduct points
              </label>
            </div>
          </div>

          {/* Quantity */}
          <div className="cdp-modal__field">
            <label className="cdp-modal__label">Quantity to adjust</label>
            <input
              className="cdp-modal__input"
              type="number"
              min="1"
              placeholder="e.g. 100"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>

          {/* Reason */}
          <div className="cdp-modal__field">
            <label className="cdp-modal__label">Reason for adjustment</label>
            <div className="cdp-modal__select-wrap">
              <select className="cdp-modal__select" value={reason} onChange={(e) => setReason(e.target.value)}>
                {ADJUST_REASONS.map((r) => <option key={r}>{r}</option>)}
              </select>
              <ChevronDown size={16} className="cdp-modal__select-icon" style={{ transform: 'rotate(90deg)' }} />
            </div>
          </div>

          {/* Remarks */}
          <div className="cdp-modal__field">
            <label className="cdp-modal__label">Remarks <span className="cdp-modal__optional">(optional)</span></label>
            <textarea
              className="cdp-modal__textarea"
              rows={4}
              placeholder="Add any additional notes or context for this adjustment."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>
        </div>

        <div className="cdp-modal__footer">
          <button className="cdp-modal__cancel" onClick={onClose}>Cancel</button>
          <button className="cdp-modal__confirm" disabled={!quantity}>Confirm adjustment</button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Suspend Modal ────────────────────────────────────────────────────────────

function SuspendModal({ customer, onClose, onConfirm }) {
  const [reason, setReason] = useState('')

  return createPortal(
    <div className="cdp-modal-overlay" onClick={onClose}>
      <div className="cdp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cdp-modal__header">
          <h3>Suspend customer account</h3>
          <button className="cdp-modal__close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="cdp-modal__body">
          <p className="cdp-modal__suspend-desc">
            This action will temporarily disable the customer's account for this outlet only
          </p>

          {/* Customer card */}
          <div className="cdp-modal__customer-card">
            <img src={customer.avatar} alt={customer.name} />
            <div>
              <p className="cdp-modal__card-name">{customer.name}</p>
              <p className="cdp-modal__card-pts"><Star size={14} className="cdp-modal__star" /> {customer.points.toLocaleString()} pts · Total {customer.orders} orders</p>
            </div>
            <span className="cdp-modal__card-status customers-status customers-status--active">• Active</span>
          </div>

          {/* Consequences */}
          <ul className="cdp-modal__consequences">
            <li>The voucher code will stop working immediately.</li>
            <li>All usage history will be permanently erased</li>
            <li>Customers with this code can no longer redeem it</li>
          </ul>

          {/* Reason */}
          <div className="cdp-modal__field">
            <label className="cdp-modal__label">Reason for suspension</label>
            <textarea
              className="cdp-modal__textarea"
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <div className="cdp-modal__footer">
          <button className="cdp-modal__cancel" onClick={onClose}>Cancel</button>
          <button className="cdp-modal__confirm cdp-modal__confirm--danger" onClick={() => { onConfirm(); onClose() }}>
            <Ban size={18} /> Suspend account
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Details panel tabs ───────────────────────────────────────────────────────

function OverviewTab({ customer, onAdjustPoints, onSuspend }) {
  return (
    <div className="cdp-tab-content">
      <p className="cdp-section-label">BASIC INFO</p>
      <div className="cdp-info-list">
        <div className="cdp-info-row"><span>Name:</span><strong>{customer.name}</strong></div>
        <div className="cdp-info-row"><span>Phone number:</span><strong>{customer.phone}</strong></div>
        <div className="cdp-info-row"><span>Email address:</span><strong>{customer.email || '-'}</strong></div>
        <div className="cdp-info-row"><span>Signup source:</span><strong>{customer.signupSource}</strong></div>
        <div className="cdp-info-row"><span>Created:</span><strong>{customer.created}</strong></div>
      </div>

      <p className="cdp-section-label" style={{ marginTop: 28 }}>STATS</p>
      <div className="cdp-stats-grid">
        <div className="cdp-stat"><span>ORDERS</span><strong>{customer.orders}</strong></div>
        <div className="cdp-stat"><span>TOTAL SPENT</span><strong>{customer.totalSpend}</strong></div>
        <div className="cdp-stat"><span>POINTS BALANCE</span><strong>{customer.points.toLocaleString()} pts</strong></div>
        <div className="cdp-stat"><span>AVG ORDERS</span><strong>{customer.avgOrder}</strong></div>
      </div>

      <p className="cdp-section-label" style={{ marginTop: 28 }}>ACTIONS</p>
      <div className="cdp-actions">
        <button className="cdp-action-btn" onClick={onAdjustPoints}>
          <Star size={16} /> Adjust points manually
        </button>
        <button className="cdp-action-btn cdp-action-btn--danger" onClick={onSuspend}>
          <Ban size={16} /> Suspend account
        </button>
      </div>
    </div>
  )
}

function OrdersTab({ customer }) {
  const orders = CUSTOMER_ORDERS[customer.id] || []

  const posStatusClass = (s) => {
    if (s === 'open')      return 'cdp-order-status--open'
    if (s === 'closed')    return 'cdp-order-status--closed'
    if (s === 'cancelled') return 'cdp-order-status--cancelled'
    return ''
  }

  const typeClass = (t) => {
    if (t === 'Dine-in') return 'cdp-order-type--dinein'
    if (t === 'Pickup')  return 'cdp-order-type--pickup'
    if (t === 'Kiosk')   return 'cdp-order-type--kiosk'
    return ''
  }

  return (
    <div className="cdp-tab-content">
      <div className="cdp-orders-table">
        <div className="cdp-orders-table__head">
          <span>ORDER ID</span>
          <span>TOTAL</span>
          <span>PLACED</span>
        </div>

        {orders.map((order) => (
          <div key={order.id} className="cdp-orders-table__row">
            <div>
              <p className="cdp-orders-table__id">{order.id}</p>
              <span className={`cdp-order-type ${typeClass(order.type)}`}>• {order.type}</span>
            </div>
            <span className={`cdp-order-status ${posStatusClass(order.posStatus)}`}>
              • {order.posStatus.charAt(0).toUpperCase() + order.posStatus.slice(1)}
            </span>
            <span className="cdp-orders-table__total">{order.total}</span>
            <span className="cdp-orders-table__placed">{order.placed}</span>
          </div>
        ))}

        {orders.length === 0 && (
          <p className="cdp-empty">No orders found.</p>
        )}
      </div>

      <button className="cdp-view-all">View all orders</button>
    </div>
  )
}

function PointsTab({ customer, onAdjustPoints }) {
  const history = CUSTOMER_POINTS[customer.id] || []

  return (
    <div className="cdp-tab-content">
      <div className="cdp-points-header">
        <div>
          <p className="cdp-section-label">POINTS BALANCE</p>
          <p className="cdp-points-balance">{customer.points.toLocaleString()} pts</p>
        </div>
        <button className="cdp-adjust-btn" onClick={onAdjustPoints}>Adjust points</button>
      </div>

      <p className="cdp-section-label" style={{ marginTop: 28 }}>POINTS HISTORY</p>
      <div className="cdp-points-list">
        {history.map((entry, i) => (
          <div key={i} className="cdp-points-entry">
            <div className="cdp-points-entry__left">
              <p className="cdp-points-entry__label">{entry.label}</p>
              <p className="cdp-points-entry__date">{entry.date}</p>
              {entry.reason && <p className="cdp-points-entry__reason">Reason:  {entry.reason}</p>}
            </div>
            <div className="cdp-points-entry__right">
              <p className={`cdp-points-entry__delta ${entry.delta > 0 ? 'cdp-points-entry__delta--plus' : 'cdp-points-entry__delta--minus'}`}>
                {entry.delta > 0 ? `+${entry.delta}` : entry.delta}
              </p>
              <p className="cdp-points-entry__balance">Balance: {entry.balance}</p>
            </div>
          </div>
        ))}

        {history.length === 0 && <p className="cdp-empty">No points history.</p>}
      </div>
    </div>
  )
}

// ─── Details Panel ────────────────────────────────────────────────────────────

function CustomerDetailsPanel({ customer, onClose }) {
  const [activeTab, setActiveTab] = useState('overview')
  const [showAdjust, setShowAdjust]   = useState(false)
  const [showSuspend, setShowSuspend] = useState(false)

  return (
    <>
      <div className="customers-details-overlay" onClick={onClose} />

      <aside className="customers-details-panel">
        {/* Top bar */}
        <div className="customers-details-panel__header">
          <h3>Customer details</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        {/* Customer hero */}
        <div className="cdp-hero">
          <img src={customer.avatar} alt={customer.name} className="cdp-hero__avatar" />
          <div className="cdp-hero__info">
            <div className="cdp-hero__name-row">
              <h4 className="cdp-hero__name">{customer.name}</h4>
              <span className={`customers-status customers-status--${customer.status}`}>• {customer.status.charAt(0).toUpperCase() + customer.status.slice(1)}</span>
            </div>
            <p className="cdp-hero__meta">
              <Star size={13} className="cdp-hero__star" />
              {customer.points.toLocaleString()} pts
              <span className="cdp-hero__sep">•</span>
              {customer.phone}
              <span className="cdp-hero__sep">•</span>
              Member since {customer.memberSince}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="cdp-tabs">
          {['overview', 'orders', 'points'].map((tab) => (
            <button
              key={tab}
              className={`cdp-tab ${activeTab === tab ? 'cdp-tab--active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="customers-details-panel__body">
          {activeTab === 'overview' && (
            <OverviewTab
              customer={customer}
              onAdjustPoints={() => setShowAdjust(true)}
              onSuspend={() => setShowSuspend(true)}
            />
          )}
          {activeTab === 'orders' && <OrdersTab customer={customer} />}
          {activeTab === 'points' && (
            <PointsTab customer={customer} onAdjustPoints={() => setShowAdjust(true)} />
          )}
        </div>
      </aside>

      {showAdjust && (
        <AdjustPointsModal customer={customer} onClose={() => setShowAdjust(false)} />
      )}
      {showSuspend && (
        <SuspendModal customer={customer} onClose={() => setShowSuspend(false)} onConfirm={() => {}} />
      )}
    </>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CustomersPage() {
  const [selectedCustomer, setSelectedCustomer] = useState(null)

  return (
    <div className="customers-layout">
      <main className="customers-page">
        <div className="customers-page__header">
          <div>
            <div className="customers-page__breadcrumb">Hikaya House / Customer</div>
            <h1>Customers</h1>
            <p>KLCC Branch · 1,291 registered customer</p>
          </div>
          <button className="customers-page__export-btn">
            <Download size={18} /> Export CSV
          </button>
        </div>

        <div className="customers-page__tabs">
          <button className="active">All customers (1,291)</button>
          <button>Active (1,284)</button>
          <button>Suspended (5)</button>
          <button>Inactive (2)</button>
        </div>

        <div className="customers-page__filters">
          <div className="customers-page__filters-left">
            <button className="customers-page__filter-btn">Source: All</button>
            <button className="customers-page__filter-btn">Sort: Most orders</button>
            <button className="customers-page__filter-btn">Filters (1)</button>
          </div>
          <div className="customers-page__search">
            <Search size={18} />
            <input placeholder="Search users" />
          </div>
        </div>

        <div className="customers-table">
          <div className="customers-table__header">
            <div>NAME</div>
            <div>PHONE</div>
            <div>STATUS</div>
            <div>POINTS</div>
            <div>SOURCE</div>
            <div>ORDERS</div>
            <div>LAST LOGIN</div>
            <div />
          </div>

          {customers.map((customer) => (
            <button
              key={customer.id}
              className="customers-table__row"
              onClick={() => setSelectedCustomer(customer)}
            >
              <div className="customers-table__customer">
                <img src={customer.avatar} alt={customer.name} />
                <div>
                  <h4>{customer.name}</h4>
                  <p>{customer.email}</p>
                </div>
              </div>
              <div>{customer.phone}</div>
              <div>
                <span className={`customers-status customers-status--${customer.status}`}>
                  • {customer.status}
                </span>
              </div>
              <div>{customer.points.toLocaleString()}</div>
              <div>
                <span className={`customers-source customers-source--${customer.source.toLowerCase().replace('-', '')}`}>
                  • {customer.source}
                </span>
              </div>
              <div>{customer.orders}</div>
              <div>{customer.lastLogin}</div>
              <div className="customers-table__arrow">
                <ChevronDown size={18} />
              </div>
            </button>
          ))}
        </div>
      </main>

      {selectedCustomer && (
        <CustomerDetailsPanel
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      )}
    </div>
  )
}